// @ts-check
import { getCasePropSelector } from '../utils/case-prop-selector'
import { isRequiredCheckpoint } from '../utils/is-required-checkpoint'
import { toNumber } from '../utils/numbers'
import { createSelector } from '../utils/selector-utils'
import { selectVitalsTrackingEnabled } from './case-props'
import { eventSelectors } from './events'
import { optionSelectIndividualVitalTypes } from './vital-type'
import { vitalsSelectors } from './vitals'
import { selectBaseVitalsInterval } from './vitals-interval'

const root = 'vitalsCheckpoints'
/**
 * @param {number} time
 * @param {boolean} [complete]
 * @returns {any}
 */
export const actionAddVitalsCheckpoint = (time, complete) => {
  time = toNumber(time)
  const cp = { time }
  if (complete) cp.complete = true
  return { [`${root}.cp_${time}`]: cp }
}
/**
 * @type {(id: string, complete: boolean) => any}
 * @returns {any}
 */
export const actionUpdateCheckpointComplete = (id, complete) => ({
  [`${root}.${id}.complete`]: complete ? true : null,
})

/** @type {CaseSelector<CaseData['vitalsCheckpoints'] | {}>} */
export const selectVitalsCheckpointsRaw = getCasePropSelector(root, {})

/** @type {CaseSelector<{ id: string; time: number; complete: boolean }[]>} */
export const selectVitalsCheckpointsRawArray = createSelector(
  selectVitalsCheckpointsRaw,
  raw => {
    const result = []
    for (const id in raw) {
      const { time, complete } = raw[id]
      if (!time) continue
      result.push({
        id,
        time,
        complete: !!complete,
      })
    }
    result.sort((a, b) => a.time - b.time)
    return result
  }
)

/**
 * Returns a reasonable number for vitals duration based on caseStart time,
 * appTime, and maximum allowed duration.
 *
 * It will use the lesser of appTime and vitalsEnd (hence the "no future" part).
 *
 * It will never return a negative number, and will never return a number
 * greater than the maxDuration.
 *
 * @param {CaseData} caseData
 * @param {{
 *   appTime: number
 *   maxDuration: number
 * }} param1
 * @returns {number}
 */
export const optionSelectVitalsDurationNoFuture = (
  caseData,
  { appTime, maxDuration }
) => {
  const caseStartTime = eventSelectors.selectCaseStartTime(caseData)
  const vitalsEndTime = eventSelectors.selectVitalsEndTime(caseData)
  if (!caseStartTime) {
    return 0
  }
  const timeToUseAsEnd = Math.min(appTime, vitalsEndTime || appTime)
  // never less than 0, never more than maxDuration
  return Math.max(0, Math.min(maxDuration, timeToUseAsEnd - caseStartTime))
}

/**
 * @param {CaseData} caseData
 * @param {{
 *   appTime: number
 *   maxDuration: number
 * }} param1
 * @returns {number}
 */
export const optionSelectNumberOfRequiredCheckpoints = (
  caseData,
  { appTime, maxDuration }
) => {
  const baseInterval = selectBaseVitalsInterval(caseData)
  const duration = optionSelectVitalsDurationNoFuture(caseData, {
    appTime,
    maxDuration,
  })
  if (!duration) {
    return 1
  }
  return Math.floor(duration / (baseInterval * 60000)) + 1
}

/**
 * @param {CaseData} caseData
 * @param {{ appTime: number }} param1
 */
export const optionSelectVitalsCheckpointsRawArrayNoFuture = (
  caseData,
  { appTime }
) =>
  selectVitalsCheckpointsRawArray(caseData).filter(
    ({ time }) => time <= appTime
  )

/**
 * @param {CaseData} caseData
 * @param {{
 *   appTime: number
 *   maxDuration: number
 * }} param1
 * @returns {number[]}
 */
export const optionSelectRequiredCheckpointTimes = (
  caseData,
  { appTime, maxDuration }
) => {
  const caseStartTime = eventSelectors.selectCaseStartTime(caseData)
  const intervalMinutes = selectBaseVitalsInterval(caseData)
  const numberOfCheckpoints = optionSelectNumberOfRequiredCheckpoints(
    caseData,
    { appTime, maxDuration }
  )
  if (!caseStartTime) {
    return []
  }
  const numberToCreate = numberOfCheckpoints
  const result = []

  for (let i = 0; i < numberToCreate; i++) {
    result.push(caseStartTime + i * (60000 * intervalMinutes))
  }
  return result
}

/**
 * @param {CaseData} caseData
 * @param {Pick<
 *   ReportRenderingInputs,
 *   'appTime' | 'maxDuration' | 'tempUnit' | 'activeCheckpointId'
 * >} opts
 * @returns {Checkpoint[]}
 */
export const optionSelectVitalsCheckpoints = (
  caseData,
  { appTime, maxDuration, activeCheckpointId, tempUnit }
) => {
  const caseStart = eventSelectors.selectCaseStartTime(caseData)
  const baseInterval = selectBaseVitalsInterval(caseData)
  const checkpointsBaseArray = optionSelectVitalsCheckpointsRawArrayNoFuture(
    caseData,
    { appTime }
  )
  const individualVitalTypes = optionSelectIndividualVitalTypes(caseData, {
    tempUnit,
  })
  const records = vitalsSelectors.optionSelectVitalRecords(caseData, {
    tempUnit,
  })
  const autoRecords = vitalsSelectors.optionSelectVitalAutoRecords(caseData, {
    tempUnit,
  })
  const vitalsTrackingEnabled = selectVitalsTrackingEnabled(caseData)
  const vitalsDuration = optionSelectVitalsDurationNoFuture(caseData, {
    appTime,
    maxDuration,
  })

  // we build this object up for each possible vital
  // to ensure we start with `null` values for type-safe
  // checks later.
  // Otherwise logic down the road can't safely check for `=== null`
  const previousValues = {}
  for (const vitalType of individualVitalTypes) {
    previousValues[vitalType.id] = null
  }

  // If we are tracking vitals and have no checkpoints yet we need an initial one
  // But if we are not tracking vitals we don't need one.
  const defaultCheckpoints = vitalsTrackingEnabled
    ? [{ id: 'cp_0', time: null, extra: false, complete: false }]
    : []

  const checkpoints = checkpointsBaseArray.length
    ? checkpointsBaseArray
    : defaultCheckpoints

  const toReturn = []
  let index = 0
  for (const cp of checkpoints) {
    const cpId = cp.id
    const isComplete = !!cp.complete
    const isFirst = index === 0
    const isLast = index === checkpoints.length - 1
    const autoRecordsForCheckpoint = autoRecords[cpId] || null
    const selected = activeCheckpointId === cpId
    /** @type {{ [id: string]: CheckpointValueVital }} */
    const values = {}
    const vitalsInUse = []

    // increment index
    index++

    let hasAllRelevantValues = true
    let isBlank = true

    for (const vitalType of individualVitalTypes) {
      const { id, startValue, fillFromPrevious } = vitalType

      const recordedValue =
        records && records[cpId] && records[cpId].hasOwnProperty(id)
          ? records[cpId][id]
          : null

      // grab previous value if relevant
      const previousValue =
        previousValues[id] !== null && previousValues[id] !== 'skipped'
          ? previousValues[id]
          : null

      const autoRecordForVital =
        (autoRecordsForCheckpoint && autoRecordsForCheckpoint[id]) || null

      /**
       * NB: It's important that we consider those filled from previous to still
       * be "blank" because we use that property to determine what checkpoints
       * can be deleted if a vitals end time is set. Of course those auto filled
       * from previous will have "a value" but it is not a "recorded" value so
       * we still consider it "blank" for the purposes of deletion.
       */
      if (autoRecordForVital || recordedValue != null) {
        isBlank = false
      }

      const isPlaced = vitalType.placed

      /** @type {CheckpointVitalValueSource} */
      let valueSource = 'manual'
      let value = null
      let connectionId
      let monitorType
      let autoValue = null
      let autoValueTime = null

      // if we have an auto value, use it
      if (autoRecordForVital !== null) {
        valueSource = 'auto'
        value = autoValue = autoRecordForVital.value
        connectionId = autoRecordForVital.connectionId
        monitorType = autoRecordForVital.monitorType
        autoValueTime = autoRecordForVital.time
      } else if (id === 'map') {
        const sbpValueObj = values.sbp
        const dbpValueObj = values.dbp
        /** @type {number | string | null} */
        let sbpValue = (sbpValueObj && sbpValueObj.value) || null
        /** @type {number | string | null} */
        let dbpValue = (dbpValueObj && dbpValueObj.value) || null

        const hasBpValues = !!(sbpValue && dbpValue)

        if (isFirst && hasBpValues) {
          sbpValue = sbpValueObj && sbpValueObj.startValueForVital
          dbpValue = dbpValueObj && dbpValueObj.startValueForVital
        }

        if (sbpValue !== null && dbpValue !== null) {
          if (sbpValue === 'skipped' || dbpValue === 'skipped') {
            value = 'skipped'
          } else {
            // calculate MAP
            // @ts-ignore
            value = Math.round((dbpValue * 2 + sbpValue) / 3)
          }
          valueSource = 'calculated'
        }
      }

      // If we are doing auto vitals, and this is one
      // that we can fill from previous we do that.
      if (fillFromPrevious && isPlaced && previousValue !== null) {
        valueSource = 'previous'
        value = previousValue
      }

      // any manually recorded value always wins
      if (recordedValue !== null) {
        if (valueSource === 'previous') {
          valueSource = 'manual'
        } else {
          valueSource = value !== null ? 'override' : 'manual'
        }
        value = recordedValue
      }

      const hasValue = value != null
      const isSkipped = value === 'skipped'

      // store as "previous" for the next round
      if (hasValue && !isSkipped) {
        previousValues[id] = value
      }

      if ((!isPlaced && !hasValue) || vitalType.status === 'ignored') {
        continue
      } else {
        vitalsInUse.push(vitalType)
      }

      /** @type {CheckpointValueVital} */
      const toReturn = {
        previousValue,
        startValueForVital: startValue,
        autoValue,
        autoValueTime,
        connectionId,
        monitorType,
        recordedValue,
        value,
        valueSource,
      }

      if (!hasValue) {
        hasAllRelevantValues = false
      }

      // capture it
      values[id] = toReturn
    }

    // end of vitals loop now stuff that applies to whole checkpoint
    /** @type {Checkpoint} */
    const finalCheckpointObject = {
      id: cpId,
      extra: !!(
        cp.time &&
        !isRequiredCheckpoint({
          caseStart,
          baseInterval,
          cpTime: cp.time,
          vitalsDuration,
        })
      ),
      time: cp.time,
      isFirst,
      isLast,
      selected,
      vitalsInUse,
      values,
      isComplete,
      hasAllRelevantValues,
      hasAnySelectedVitals: vitalsInUse.length > 0,
      isBlank,
    }

    toReturn.push(finalCheckpointObject)
  }

  return toReturn
}
